<script lang="ts">
/**
 * The four states an autosaving editor moves through. Named here so the stores
 * that hold one (a report draft, a file's notes) and the strip that renders it
 * agree on the vocabulary.
 */
export type GaiaSaveStateValue = 'saved' | 'pending' | 'saving' | 'error';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import { gaiaTextButtonOverrides } from '../themes/tokens';

const props = defineProps({
  state: {
    type: String as PropType<GaiaSaveStateValue>,
    required: true,
  },
  /**
   * Suppress the save-now affordance even while the draft is dirty — for an
   * editor that is temporarily read-only (the report while its agent writes),
   * where the draft has already been flushed and there is nothing to send.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ save: [] }>();

const HINTS: Record<GaiaSaveStateValue, string> = {
  saved: 'Saved',
  pending: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Failed to save',
};

const hint = computed(() => HINTS[props.state]);

/**
 * A dirty (or failed) draft can be sent now instead of waiting out the
 * debounce; a clean one has nothing to send, so the hint stays plain text
 * rather than offering a button that would do nothing.
 */
const canSaveNow = computed(
  () => !props.disabled && (props.state === 'pending' || props.state === 'error')
);
</script>

<template>
  <!-- The autosave hint an editor pane carries in its toolbar: plain copy while
       there is nothing to do, and a text button — red once a save has failed —
       the moment there is. Shared so the report and the file notes, which sit
       one drawer apart, never disagree about what "Saved" looks like. -->
  <gaia-tooltip v-if="canSaveNow" placement="top" :delay="300">
    <template #trigger>
      <gaia-button
        text
        size="small"
        :theme-overrides="
          props.state === 'error' ? gaiaTextButtonOverrides.danger : gaiaTextButtonOverrides.ink
        "
        :style="{ flex: 'none' }"
        @click="emit('save')"
      >
        {{ hint }}
      </gaia-button>
    </template>
    Save now
  </gaia-tooltip>
  <gaia-text v-else size="micro" tone="subtle" :style="{ flex: 'none' }">{{ hint }}</gaia-text>
</template>
